/*
 * Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React from "react";
import Grid from "@mui/material/Grid";
import { convertToFloat, formValue } from "../../../services/formServiceFunctions";
import _ from "lodash";
import PropertyLoan from "./PropertyLoan/PropertyLoan";
import useForm from "../../../hooks/useForm";
import { requiredFieldDefault } from "../../../services/validationRules";
import ValidatorForm from "../../Validator/ValidatorForm";
import SubmitButton from "../../Button/SubmitButton";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import MenuItem from "@mui/material/MenuItem";
import ValidatorNumberField from "../../Validator/ValidatorNumberField";
import { Box, SelectChangeEvent, TextField } from "@mui/material";
import { processCostSupportStyle } from "../processCostSupportStyle";
import { ProcessCostSupportFormProps } from "../../../types/ProcessCostSupport";
import { ThousandsNumberInputFloat } from "../../FormFields/MaskedInputs/ThousandsNumberInputFloat";

const LivingType = ({ pcs, onSubmit, isLoading }: ProcessCostSupportFormProps) => {
  const empty = {
    date: "",
    borrowers: "",
    bank: "",
    remainingTerm: "",
    outstandingDebt: "",
    repayment: "",
  };

  const initialValues = {
    livingType: pcs.livingType,
    appartmentSize: pcs.appartmentSize,
    numberRooms: pcs.numberRooms,
    numberRoommates: pcs.numberRoommates,
    basicRentOrRepayment: pcs.basicRentOrRepayment,
    heatingCosts: pcs.heatingCosts,
    additionalCosts: pcs.additionalCosts,
    costPaidByPetitioner: pcs.costPaidByPetitioner,
    hasPropertyLoans: pcs.hasPropertyLoans,
    propertyLoans: !_.isEmpty(pcs.propertyLoans) ? pcs.propertyLoans : [empty],
  };

  const { values, errors, handleChange, handleSubmit, handleBlur, registerValidators, touchedValues } = useForm({
    initialValues,
    onSubmit,
    identifier: pcs,
  });

  const getFullCosts = () => {
    const fullCosts =
      convertToFloat(values.basicRentOrRepayment) +
      convertToFloat(values.heatingCosts) +
      convertToFloat(values.additionalCosts);
    return _.isNaN(fullCosts) ? "" : _.replace(_.round(fullCosts, 2).toString(), ".", ",");
  };

  const add = () => {
    const propertyLoans = values.propertyLoans || [];
    if (propertyLoans.length >= 2) {
      return;
    }

    propertyLoans.push(empty);

    handleChange({
      target: {
        type: "other",
        name: "propertyLoans",
        value: propertyLoans,
      },
    });
  };

  const handleMore = ({ target }: SelectChangeEvent) => {
    const { name, value } = target;
    if (value === "yes") {
      add();
    }
    if (value === "no") {
      const splitName = _.split(name, "-");
      const lastPart = splitName[splitName.length - 1];
      const key = parseInt(lastPart);
      handleChange({
        target: {
          type: "other",
          name: "propertyLoans",
          value: _.slice(values.propertyLoans, 0, key + 1),
        },
      });
    }
  };

  return (
    <>
      <Box sx={processCostSupportStyle.sectionHeadline}>Wohnen</Box>
      <ValidatorForm onSubmit={handleSubmit}>
        <Grid container sx={processCostSupportStyle.grid}>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Wohnen Sie zur Miete oder sind Sie Eigentümer?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorSelect
              label={"Wohnart"}
              name={"livingType"}
              value={formValue(values, "livingType")}
              onChange={handleChange}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              dependentValidationFields={["hasPropertyLoans"]}
            >
              <MenuItem value={"rent"}>Ich wohne zur Miete</MenuItem>
              <MenuItem value={"owner"}>Ich bin Eigentümer</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Bitte geben Sie genauere Details zum Wohnraum an, in dem Sie allein oder gemeinsam mit anderen Personen
            wohnen.
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorNumberField
              fullWidth={true}
              id="appartmentSize"
              name="appartmentSize"
              label="Größe in Quadratmetern"
              onBlur={handleBlur}
              validators={requiredFieldDefault}
              isHighlighted={true}
              registerValidators={registerValidators}
              error={!!errors["appartmentSize"]}
              helperText={errors["appartmentSize"]}
              margin="normal"
              value={formValue(values, "appartmentSize")}
              onChange={handleChange}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorNumberField
              fullWidth={true}
              id="numberRooms"
              name="numberRooms"
              label="Anzahl Zimmer"
              onBlur={handleBlur}
              validators={requiredFieldDefault}
              isHighlighted={true}
              registerValidators={registerValidators}
              error={!!errors["numberRooms"]}
              helperText={errors["numberRooms"]}
              margin="normal"
              value={formValue(values, "numberRooms")}
              onChange={handleChange}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorNumberField
              fullWidth={true}
              id="numberRoommates"
              name="numberRoommates"
              label="Anzahl Mitbewohner"
              onBlur={handleBlur}
              validators={requiredFieldDefault}
              isHighlighted={true}
              registerValidators={registerValidators}
              error={!!errors["numberRoommates"]}
              helperText={errors["numberRoommates"]}
              margin="normal"
              value={formValue(values, "numberRoommates")}
              onChange={handleChange}
            />
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            {values.livingType === "rent"
              ? "Bitte machen Sie genauere Angaben zu den Wohnkosten. Alle Angaben können Sie Ihrem Mietvertrag entnehmen"
              : "Bitte machen Sie genauere Angaben zu den Kosten für Ihren Wohnraum."}
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorNumberField
              fieldType={"money"}
              numberType={"float"}
              fullWidth={true}
              id="basicRentOrRepayment"
              name="basicRentOrRepayment"
              label={values.livingType === "rent" ? "Miete ohne Nebenkosten" : "Zinsen und Tilgung"}
              onBlur={handleBlur}
              validators={requiredFieldDefault}
              isHighlighted={true}
              registerValidators={registerValidators}
              error={!!errors["basicRentOrRepayment"]}
              helperText={errors["basicRentOrRepayment"]}
              margin="normal"
              value={formValue(values, "basicRentOrRepayment")}
              onChange={handleChange}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorNumberField
              fieldType={"money"}
              numberType={"float"}
              fullWidth={true}
              id="heatingCosts"
              name="heatingCosts"
              label="Heizungskosten"
              onBlur={handleBlur}
              validators={requiredFieldDefault}
              isHighlighted={true}
              registerValidators={registerValidators}
              error={!!errors["heatingCosts"]}
              helperText={errors["heatingCosts"]}
              margin="normal"
              value={formValue(values, "heatingCosts")}
              onChange={handleChange}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorNumberField
              fieldType={"money"}
              numberType={"float"}
              fullWidth={true}
              id="additionalCosts"
              name="additionalCosts"
              label="Übrige Nebenkosten"
              onBlur={handleBlur}
              validators={requiredFieldDefault}
              isHighlighted={true}
              registerValidators={registerValidators}
              error={!!errors["additionalCosts"]}
              helperText={errors["additionalCosts"]}
              margin="normal"
              value={formValue(values, "additionalCosts")}
              onChange={handleChange}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <TextField
              label={values.livingType === "rent" ? "Gesamtmiete" : "Gesamtbetrag monatliche Kosten"}
              value={getFullCosts()}
              fullWidth={true}
              disabled={true}
              name={"disabledCalculationField"}
              margin="normal"
              slotProps={{
                input: {
                  inputComponent: ThousandsNumberInputFloat,
                }
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorNumberField
              fieldType={"money"}
              numberType={"float"}
              fullWidth={true}
              id="costPaidByPetitioner"
              name="costPaidByPetitioner"
              label="Ich zahle davon"
              onBlur={handleBlur}
              validators={requiredFieldDefault}
              isHighlighted={true}
              registerValidators={registerValidators}
              error={!!errors["costPaidByPetitioner"]}
              helperText={errors["costPaidByPetitioner"]}
              margin="normal"
              value={formValue(values, "costPaidByPetitioner")}
              onChange={handleChange}
            />
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Zahlen Sie Kredite ab, die Sie für den Bau, Kauf oder den Erhalt der Immobilie aufgenommen haben?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorSelect
              label={"Kredite"}
              name={"hasPropertyLoans"}
              value={formValue(values, "hasPropertyLoans")}
              registerValidators={registerValidators}
              validators={formValue(values, "livingType") === "owner" ? requiredFieldDefault : []}
              error={!!errors["hasPropertyLoans"]}
              helperText={errors["hasPropertyLoans"]}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={formValue(values, "livingType") !== "owner"}
            >
              <MenuItem value={"no"}>Nein</MenuItem>
              <MenuItem value={"yes"}>Ja</MenuItem>
            </ValidatorSelect>
          </Grid>
          {formValue(values, "hasPropertyLoans") === "yes" && (
            <>
              {_.map(values.propertyLoans, (propertyLoan, key) => (
                <React.Fragment key={key}>
                  <PropertyLoan
                    path={`propertyLoans[${key}]`}
                    propertyLoan={propertyLoan}
                    handleChange={handleChange}
                  />
                  {parseInt(key) < 1 && (
                    <>
                      <Grid sx={processCostSupportStyle.question} size={12}>
                        Haben Sie einen weiteren Kredit, den Sie für den Bau, Kauf oder den Erhalt der Immobilie
                        aufgenommen haben?
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          md: 6,
                          lg: 4
                        }}>
                        <ValidatorSelect
                          label={"Weiterer Kredit?"}
                          name={`hasMore-${key}`}
                          value={formValue(values, "propertyLoans").length - 1 > parseInt(key) ? "yes" : "no"}
                          onChange={handleMore}
                          onBlur={handleBlur}
                        >
                          <MenuItem value={"no"}>Nein</MenuItem>
                          <MenuItem value={"yes"}>Ja</MenuItem>
                        </ValidatorSelect>
                      </Grid>
                    </>
                  )}
                </React.Fragment>
              ))}
            </>
          )}
          <Grid size={12}>
            <SubmitButton
              error={_.keys(errors).length > 0}
              isLoading={isLoading}
              disabled={_.keys(touchedValues).length === 0}
              variant={"contained"}
              type={"submit"}
            >
              Änderungen speichern
            </SubmitButton>
          </Grid>
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default LivingType;
